import { History, Pause, Phone, PhoneIncoming, PhoneOff, PhoneOutgoing, Repeat, Users, X } from "lucide-react";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Callout, EmptyState, Spinner } from "@/components/ui/feedback";
import { Page } from "@/components/layout/page";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { counterparty, rangeSince } from "@/lib/calls";
import { isDialable } from "@/lib/phone-number";
import {
  callBackPath, callBackTitle, callsBeforeText, clockTime, colleagueFor, daySummary, legText,
  missedCallNumber, missedCallText, missedCallWho, ownNumber,
} from "@/lib/phone-page";
import {
  useCalls, useCallSummary, useColleagues, useMissedCalls, type CallFilters,
} from "@/lib/queries";
import { directRoomCallId, inviteText } from "@/lib/rooms";
import { OWN_NON_CALL_LEGS, type Cdr, type MissedCall } from "@/lib/types";
import { cn, formatDuration, formatPhoneNumber, relativeTime } from "@/lib/utils";
import {
  AcdCallStrip, ConsultBar, DispositionView, MonitorBar, useJoiningListenIn,
} from "@/softphone/acd-call";
import { AvailabilityMenu, useRepAvailability } from "@/softphone/availability";
import { canDialSecondLine, dialBlockedReason, isEngaged } from "@/softphone/call-control";
import {
  CallControls, describeParty, EndCallButton, IncomingBanner, KeypadView, ListenInConnecting,
  ParkedRow, RingingMark,
  Section, stateText, TransferDialog, typingElsewhere, type SubView,
} from "@/softphone/call-views";
import {
  BringColleagueSection, CallRoster, DirectConsultBar, InviteStrip, useCallerHeld, useDirectRoom,
  useRoomPeople,
} from "@/softphone/direct-call";
import { AddParticipantDialog, ParticipantBar } from "@/softphone/participant";
import { DialerView, useDialerWork } from "@/softphone/dialer-offer";
import { Dialler } from "@/softphone/dialler";
import {
  ManualCallView, useManualCallWork, useOutboundDial, useOwnManualRinging,
} from "@/softphone/manual-call";
import { BlockCallerAction } from "@/softphone/block-caller";
import { LeadCardPanel } from "@/softphone/lead-card";
import { ConnectView, PhoneMenu, StandbyView } from "@/softphone/softphone-dock";
import { useCallDuration, useSoftphone, type Call } from "@/softphone/softphone-context";
import { useMarkStage } from "@/softphone/stage";
import { TranscriptPanel, useCallTranscription } from "@/softphone/transcription-widget";

/**
 * The Phone page: the same phone as the panel at the right of every screen, at full size.
 *
 * The panel is a 320px column, and a call is more than fits in one: who this is, the people on
 * the call, what to do with them, what was said, and the day around it. Here that has room. One
 * frame, three regions — a strip along the top (the phone's own state, the numbers it answers
 * to, today's counts, the options), the stage on the left (whatever the phone is doing right
 * now, at the size it deserves), and a column at the right (the live transcript and the day's
 * calls) — and it is the same frame for every phone, with or without a campaign line, so
 * a rep who moves between the two finds every control where it was.
 *
 * Nothing on it is a second implementation of the panel. The controls, the views pushed over a
 * call, the consult bar, the roster and the colleague picker are the panel's own components
 * (`softphone/call-views.tsx`, `direct-call.tsx`, `acd-call.tsx`), laid out wider. A rule about
 * what a control may do is decided once, in those modules, and the two surfaces cannot disagree.
 *
 * While this page is on screen the panel never opens itself (`softphone/stage.ts`): everything it
 * would have opened for — the ringing call, the dialer's offer, the outcome window after an ACD
 * call — is here already, at full size, and the panel is the rep's to open if they want it.
 */
export function PhonePage() {
  const { user } = useAuth();
  const phone = useSoftphone();
  if (!user) return null;

  return (
    <Page>
      {user.signed_in_by ? (
        <Card>
          <EmptyState
            icon={PhoneOff}
            title="The phone is off"
            description={`You are signed in as ${user.display_name || user.email}, so their phone stays theirs: no calls ring here, and none go out as them. Exit to use your own.`}
          />
        </Card>
      ) : !phone.available ? (
        <Card>
          <EmptyState
            icon={PhoneOff}
            title="No phone assigned"
            description="An admin needs to assign you an endpoint before you can make or take calls. Until then the phone stays hidden."
          />
        </Card>
      ) : (
        <PhoneWorkspace
          mine={user.sip_endpoint_numbers}
          recordCalls={user.sip_endpoint_record_calls === true}
        />
      )}
    </Page>
  );
}

/**
 * The page's body, given what it needs to know about the account: exported so the state gallery
 * (`routes/dev-phone.tsx`) can render it over a pretend phone with a pretend rep.
 */
export function PhoneWorkspace({
  mine, recordCalls,
}: {
  /** Every identifier the account's phone answers to, from `/auth/me`. */
  mine: readonly string[];
  /** The account records its calls. */
  recordCalls: boolean;
}) {
  const phone = useSoftphone();
  useMarkStage();
  const [view, setView] = React.useState<SubView | null>(null);
  const stageRef = React.useRef<HTMLElement>(null);
  // The control that opened a view, so Back can hand the keyboard back to it once the call
  // view is on screen again — an effect, not a timer, because the control does not exist
  // until React has committed the swap.
  const returnFocusTo = React.useRef<SubView | null>(null);

  // A view belongs to the leg it was opened over (`softphone-dock.tsx` keeps the same rule).
  const activeId = phone.activeCallId;
  React.useEffect(() => {
    setView(null);
  }, [activeId]);
  React.useEffect(() => {
    if (view !== null || !returnFocusTo.current) return;
    const tile = returnFocusTo.current;
    returnFocusTo.current = null;
    stageRef.current?.querySelector<HTMLElement>(`[data-tile="${tile}"]`)?.focus();
  }, [view]);
  const pop = () => {
    returnFocusTo.current = view;
    setView(null);
  };
  const push = (next: SubView) => setView(next);

  const active = phone.calls.find((c) => c.id === phone.activeCallId) ?? phone.primary;
  const ringingFirst = active !== null && active.direction === "inbound" && active.state === "ringing";
  // Between calls a campaign phone has two things of its own, and the panel no longer opens over
  // this page to show them: the ACD's outcome window, and the lead the dialer is offering.
  const inWindow = useRepAvailability()?.view.state === "disposition";
  const work = useDialerWork();
  const manual = useManualCallWork();
  const ownRinging = useOwnManualRinging();
  const joining = useJoiningListenIn(active?.id ?? null);
  const onCall = active !== null && !ringingFirst;
  const registered = phone.registration === "registered";
  const today = useTodayFilters();

  const dial = () => {
    stageRef.current?.querySelector<HTMLInputElement>('input[inputmode="tel"], input')?.focus();
  };

  let stage: React.ReactNode;
  if (phone.registration === "standby") {
    stage = <div className="mx-auto max-w-sm p-8"><StandbyView /></div>;
  } else if (!registered) {
    stage = <div className="mx-auto max-w-sm p-8"><ConnectView /></div>;
  } else if (!active && inWindow) {
    // Full width, unlike the two panes around it: the outcome buttons are the only thing on
    // screen and there can be twenty of them, so the wider the stage the fewer rows to read.
    stage = <div className="p-6"><DispositionView /></div>;
  } else if (!active && manual.shown) {
    // A number the rep dialed by hand (ADR-047): placing, ringing, or how it failed.
    stage = <div className="mx-auto max-w-md p-8"><ManualCallView /></div>;
  } else if (!active && (work.offer !== null || work.dialing)) {
    stage = <div className="mx-auto max-w-md p-8"><DialerView /></div>;
  } else if (!active) {
    stage = <IdleStage />;
  } else if (ringingFirst && manual.shown && ownRinging === active.id) {
    // The ACD ringing this phone with the call the rep placed, which the phone answers itself.
    stage = <div className="mx-auto max-w-md p-8"><ManualCallView /></div>;
  } else if (ringingFirst && joining) {
    // A supervisor's listen-in, which the phone answers itself: connecting, not Answer / Decline.
    stage = <div className="mx-auto max-w-md p-8"><ListenInConnecting call={active} /></div>;
  } else if (ringingFirst) {
    stage = <IncomingStage call={active} mine={mine} />;
  } else {
    stage = (
      <CallStage
        call={active}
        view={view}
        onPush={push}
        onPop={pop}
        recordCalls={recordCalls}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {phone.error && <Callout tone="danger">{phone.error}</Callout>}
      <Chrome onCall={onCall} today={today} onDial={dial} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <section
          ref={stageRef}
          aria-label="Your phone"
          className="min-w-0"
          onKeyDown={(e) => {
            if (e.key === "Escape" && view) {
              e.stopPropagation();
              pop();
            }
          }}
        >
          <Card className="overflow-hidden">{stage}</Card>
        </section>
        <aside className="flex min-w-0 flex-col gap-4" aria-label="Beside the call">
          <TranscriptCard />
          <MyCallsTodayCard filters={today} mine={mine} />
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The strip along the top                                                                     */
/* ------------------------------------------------------------------------------------------ */

/** Today's own calls, for the counts in the strip and the card in the column. Pinned per day,
 *  since recomputing midnight on every render would make a new query key every time. */
function useTodayFilters(): CallFilters {
  const day = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => ({ mine: true, exclude_leg: OWN_NON_CALL_LEGS, since: rangeSince("today") }), [day]);
}

/**
 * A campaign rep's taking-calls control first and large (`AvailabilityMenu`, absent for a phone
 * with no lines), the options, the numbers the phone answers to, today's counts, and Dial a
 * number between calls. Whether the phone is connected is not said here: a phone that is not
 * shows Connect in place of the stage, and the call bar says what a call is doing.
 */
function Chrome({
  onCall, today, onDial,
}: {
  onCall: boolean;
  today: CallFilters;
  onDial: () => void;
}) {
  const phone = useSoftphone();
  const number = ownNumber(phone.credentials);
  const summary = useCallSummary(today, { refetchInterval: 60_000 });
  const { calls, minutes } = daySummary(summary.data);
  const dialBlocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls)) !== null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <AvailabilityMenu large />
      <PhoneMenu />
      {phone.credentials && (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone="outline">
            ext. <span className="font-mono">{phone.credentials.extension}</span>
          </Badge>
          {number && (
            <Badge tone="outline">
              Your number <span className="font-mono">{formatPhoneNumber(number)}</span>
            </Badge>
          )}
        </span>
      )}
      <span className="flex-1" />
      <Stat label="Calls today" value={String(calls)} />
      <Stat label="On the phone" value={`${minutes} min`} />
      {!onCall && (
        <Button variant="outline" disabled={dialBlocked} onClick={onDial}>
          <PhoneOutgoing />
          Dial a number
        </Button>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-1.5 tabular">
      <span className="text-xs text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text">{value}</span>
    </span>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={cn("mb-2 text-xs font-medium text-text-muted", className)}>{children}</h3>;
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: nothing in progress                                                              */
/* ------------------------------------------------------------------------------------------ */

/** Connected and idle: what the phone is, where calls to it come from, a box to dial from, and
 *  the calls that were missed. */
function IdleStage() {
  const phone = useSoftphone();
  const number = ownNumber(phone.credentials);
  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  return (
    <>
      <div className="flex flex-col items-center gap-1 px-6 pb-8 pt-12 text-center">
        <span className="mb-2 flex size-14 items-center justify-center rounded-full border-2 border-accent-9 text-accent-11">
          <Phone className="size-6" aria-hidden />
        </span>
        <p className="text-lg font-semibold text-text">Your phone is connected</p>
        <p className="text-sm text-text-muted">
          {number ? (
            <>
              Calls to <span className="font-mono">{formatPhoneNumber(number)}</span> ring here.
            </>
          ) : (
            "Calls to your extension ring here."
          )}
        </p>
        {phone.credentials && (
          <p className="text-xs text-text-muted">
            Colleagues reach you at extension{" "}
            <span className="font-mono">{phone.credentials.extension}</span>.
          </p>
        )}
      </div>
      <div className="border-t border-border" />
      <div className="grid gap-6 p-5 md:grid-cols-2">
        <div>
          <SectionLabel>Dial a number</SectionLabel>
          <Dialler
            layout="inline"
            keypadOffered={false}
            disabled={blocked !== null}
            disabledReason={blocked ?? undefined}
          />
          <p className="mt-2 text-xs text-text-muted">
            A number or an extension. Picking a colleague dials at once.
          </p>
        </div>
        <div>
          <SectionLabel>Missed calls</SectionLabel>
          <MissedCalls />
        </div>
      </div>
    </>
  );
}

/**
 * The calls this phone did not take, newest first, each with a button to call the other party
 * back. Not the day's calls: those are in the column beside the stage, and the one list a rep
 * wants in front of them between calls is the one they owe a call to.
 *
 * Read from `GET /phone/missed` (ADR-048 in `platform`), not from the call history: the history
 * is the legs this phone was on, and a call that never reached the phone — a caller on the rep's
 * own number while they were on a break or their phone was off — has no leg of theirs at all.
 * The server puts those beside the calls that rang, and says what became of each caller.
 *
 * Call back goes out the way the call came in (`callBackPath`, ADR-047): through the campaign
 * it was received on, placed by the dialer and brought back to this phone in the call's room —
 * never a plain call from the phone in its place. A rep not Ready on that campaign is told so
 * under the list, as is any refusal from the platform, where the Dial box would say it too.
 */
function MissedCalls() {
  const phone = useSoftphone();
  const colleagues = useColleagues();
  const out = useOutboundDial();
  const list = useMissedCalls({}, { refetchInterval: 60_000 });
  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  const [refusal, setRefusal] = React.useState<string | null>(null);
  // The row whose call is being placed, so only its button shows the wait.
  const [placing, setPlacing] = React.useState<string | null>(null);
  const rows = (list.data ?? []).slice(0, 5);

  const callBack = async (call: MissedCall, number: string) => {
    setRefusal(null);
    const back = callBackPath(call, out.path(number));
    if (back.via === "blocked") {
      setRefusal(back.reason);
      return;
    }
    setPlacing(call.id);
    try {
      await out.place(number, back.via === "campaign" ? back.campaign.campaign_id : null);
    } catch (e) {
      // A refusal from core-api has a sentence for the rep. A refusal from the phone itself has
      // already gone into the phone's banner, as it always has.
      if (e instanceof ApiError) setRefusal(e.message);
    } finally {
      setPlacing(null);
    }
  };

  if (list.isPending) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }
  if (list.error) {
    return (
      <p className="py-2 text-xs text-text-muted">
        {list.error instanceof ApiError ? list.error.message : "Could not load your missed calls."}
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="py-2 text-xs text-text-muted">Nothing missed. A call you did not take shows up here.</p>;
  }
  return (
    <div>
      <ul className="divide-y divide-border rounded-md border border-border">
        {rows.map((call) => {
          const number = call.caller_number;
          const who = missedCallWho(call, colleagues.data, formatPhoneNumber);
          const shownNumber = missedCallNumber(call, colleagues.data, formatPhoneNumber);
          const what = missedCallText(call);
          const back = number ? callBackPath(call, out.path(number)) : null;
          return (
            <li key={call.id} className="flex items-center gap-2.5 px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-text">
                  {who}
                  {shownNumber && (
                    <span className="ml-1.5 font-mono text-xs text-text-muted">{shownNumber}</span>
                  )}
                </span>
                <span className="block truncate text-xs text-text-muted" title={what}>
                  {what}
                </span>
              </span>
              <span className="shrink-0 text-xs tabular text-text-subtle" title={call.at}>
                {clockTime(call.at)}
              </span>
              {number && back && isDialable(number) ? (
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label={`Call ${who} back`}
                  title={blocked ?? callBackTitle(back)}
                  disabled={blocked !== null || (placing !== null && placing !== call.id)}
                  loading={placing === call.id}
                  onClick={() => void callBack(call, number)}
                >
                  <Phone />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {refusal && <p className="mt-2 text-xs text-danger-11" role="alert">{refusal}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: a call ringing                                                                   */
/* ------------------------------------------------------------------------------------------ */

/**
 * The first call ringing: who, why (a campaign, a colleague bringing this phone into their
 * call, a lead's card), what this number and this phone have to do with each other, and two
 * choices. Answer takes focus on arrival unless the rep is typing somewhere, as on the panel.
 */
function IncomingStage({ call, mine }: { call: Call; mine: readonly string[] }) {
  const phone = useSoftphone();
  const { title, subtitle, internalTransfer } = describeParty(call, phone.conference);
  const [takeFocus] = React.useState(() => !typingElsewhere());
  const others = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );
  const { room } = useDirectRoom(call.id, { ringing: true });
  const invited = inviteText(room) !== null;

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 px-6 py-10 text-center">
      <RingingMark internalTransfer={internalTransfer} />
      <div className="w-full min-w-0">
        <p className="truncate text-xl font-semibold text-text">{title}</p>
        <p className="truncate text-xs text-text-muted">Incoming call · {subtitle}</p>
      </div>
      <AcdCallStrip callId={call.id} />
      <InviteStrip callId={call.id} />
      <LeadCardPanel callId={call.id} />
      {!invited && <ContactStrip remote={call.remote} mine={mine} />}
      <div className="grid w-full grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="h-11 text-sm text-danger-11"
          onClick={() => void phone.reject(call.id)}
        >
          <X />
          Decline
        </Button>
        <Button
          variant="default"
          className="h-11 bg-success-9 text-sm hover:brightness-110"
          onClick={() => void phone.answer(call.id)}
          autoFocus={takeFocus}
        >
          <Phone />
          Answer
        </Button>
      </div>
      {/* Blocking a caller who is ringing declines them as well. */}
      <BlockCallerAction call={call} decline />
      {others.length > 0 && (
        <div className="flex w-full flex-col gap-2 text-left">
          {others.map((c) => (
            <IncomingBanner key={c.id} call={c} decision={false} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Who this number is to this phone: a colleague, or an outside number, and the calls before. */
function ContactStrip({ remote, mine }: { remote: string; mine: readonly string[] }) {
  const colleagues = useColleagues();
  const colleague = colleagueFor(remote, colleagues.data);
  const dialable = isDialable(remote);
  const history = useCalls({ mine: true, exclude_leg: OWN_NON_CALL_LEGS, q: remote }, 0, {
    enabled: dialable && colleague === null,
  });
  const last = history.data?.items[0];
  const before = history.data?.total ?? 0;
  if (!colleague && !dialable) return null;
  return (
    <div className="w-full rounded-lg border border-border px-3 py-2 text-left text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-text">
          {colleague ? (colleague.name ?? `Extension ${colleague.extension}`) : formatPhoneNumber(remote)}
        </span>
        {colleague ? (
          <Badge tone="outline" size="sm">Colleague · ext. {colleague.extension}</Badge>
        ) : (
          <Badge tone="neutral" size="sm">Outside number</Badge>
        )}
      </div>
      {!colleague && history.data && (
        <p className="mt-0.5 text-text-muted">
          {before === 0
            ? "First call with you."
            : `${before} ${before === 1 ? "call" : "calls"} with you${last ? ` · last one ${relativeTime(last.start_time)}, ${legText(last, mine).toLowerCase()}` : ""}`}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: the call the rep is on                                                          */
/* ------------------------------------------------------------------------------------------ */

/**
 * Top to bottom: the party, how long, whether it is recorded and what state it is in; the six
 * controls and End call, pinned in a row; then a second call ringing, the campaign, a briefing
 * in progress, a lead's card and any parked leg; then who this is and who else is on the call —
 * or the keypad, until Back. Transfer is a dialog over all of it.
 */
function CallStage({
  call, view, onPush, onPop, recordCalls,
}: {
  call: Call;
  view: SubView | null;
  onPush: (view: SubView) => void;
  onPop: () => void;
  recordCalls: boolean;
}) {
  const phone = useSoftphone();
  const ringingIn = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );
  const parked = phone.calls.filter((c) => c.id !== call.id && !ringingIn.includes(c));
  const direct = directRoomCallId(phone.conference) === call.id;

  return (
    <>
      <CallBar call={call} recordCalls={recordCalls} />
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-surface-sunken px-4 py-2">
        <CallControls call={call} onPush={onPush} layout="row" />
        <span className="flex-1" />
        <EndCallButton call={call} className="h-9 w-auto px-4" />
      </div>
      <div className="flex flex-col gap-3 p-5">
        {ringingIn.map((c) => (
          <IncomingBanner key={c.id} call={c} decision={c.id === phone.waiting?.id} />
        ))}
        <AcdCallStrip callId={call.id} />
        <ConsultBar />
        <DirectConsultBar callId={call.id} />
        <ParticipantBar callId={call.id} />
        <MonitorBar callId={call.id} />
        <LeadCardPanel callId={call.id} full />
        <BlockCallerAction call={call} className="self-start" />
        {parked.length > 0 && (
          <ul className="flex flex-col gap-1.5" aria-label="Other calls">
            {parked.map((c) => (
              <ParkedRow key={c.id} call={c} />
            ))}
          </ul>
        )}
        {view === "keypad" ? (
          <div className="max-w-md"><KeypadView call={call} onBack={onPop} withEndCall={false} /></div>
        ) : (
          <ContactPanel call={call} direct={direct} />
        )}
      </div>
      {/* Over the stage, not in place of it: the call this transfer is about stays behind it. */}
      <TransferDialog open={view === "transfer"} onClose={onPop} />
      <AddParticipantDialog open={view === "add"} callId={call.id} onClose={onPop} />
    </>
  );
}

function CallBar({ call, recordCalls }: { call: Call; recordCalls: boolean }) {
  const phone = useSoftphone();
  const seconds = useCallDuration(call.answeredAt);
  // On a call in a room, how many are on it as the server counts them: said once, in the subtitle.
  const people = useRoomPeople(call.id);
  const { title, subtitle, isRoom, internalTransfer } = describeParty(call, phone.conference, people);
  const transferring = phone.handingOver.includes(call.id);
  const callerHeld = useCallerHeld(call.id);
  const held = call.state === "held" || callerHeld;
  const engaged = isEngaged(call.state);
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          held ? "bg-warning-2 text-warning-11" : "bg-accent-3 text-accent-11",
        )}
      >
        {isRoom ? (
          <Users className="size-[18px]" aria-hidden />
        ) : held ? (
          <Pause className="size-[18px]" aria-hidden />
        ) : internalTransfer ? (
          <Repeat className="size-[18px]" aria-hidden />
        ) : (
          <Phone className="size-[18px]" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold text-text">{title}</p>
        <p className="truncate text-xs text-text-muted">{subtitle}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {/* The account's toggle, not the switch's verdict — and a sound test is never recorded. */}
        {recordCalls && engaged && !phone.soundTests.includes(call.id) && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-danger-11"
            title="Your account records its calls"
          >
            <span className="size-2 rounded-full bg-danger-9" aria-hidden />
            Recording
          </span>
        )}
        {engaged && (
          <span className="font-mono text-sm tabular text-text" aria-hidden>
            {formatDuration(seconds)}
          </span>
        )}
        <span className={cn("text-xs", held ? "text-warning-11" : "text-text-muted")}>
          {transferring && <Spinner className="mr-1 inline size-3 align-[-2px]" />}
          {stateText(call, transferring, callerHeld)}
        </span>
      </div>
    </div>
  );
}

/**
 * Who this is, in place of a lead's card: the name over the number, whether it is a colleague
 * or an outside number, the calls before this one; then, on a call on the rep's own number
 * (ADR-032), the people on the call and a colleague to bring in or send the caller to, across
 * the stage's whole width.
 */
function ContactPanel({ call, direct }: { call: Call; direct: boolean }) {
  const phone = useSoftphone();
  const colleagues = useColleagues();
  const colleague = colleagueFor(call.remote, colleagues.data);
  const dialable = isDialable(call.remote);
  const history = useCalls({ mine: true, exclude_leg: OWN_NON_CALL_LEGS, q: call.remote }, 0, {
    enabled: dialable,
  });
  const before = callsBeforeText(history.data?.total ?? 0);
  const { title, isRoom } = describeParty(call, phone.conference);
  const up = isEngaged(call.state);

  return (
    <div className="flex flex-col gap-4">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold tracking-tight text-text">{title}</h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {colleague ? (
            <Badge tone="outline">Colleague · ext. {colleague.extension}</Badge>
          ) : dialable ? (
            <Badge tone="neutral">Outside number</Badge>
          ) : isRoom ? (
            <Badge tone="outline">Your room</Badge>
          ) : null}
          {before && <Badge tone="outline">{before}</Badge>}
          {call.remoteName && dialable && (
            <span className="font-mono text-xs text-text-muted">{formatPhoneNumber(call.remote)}</span>
          )}
        </div>
      </div>
      <CallRoster callId={call.id} />
      {direct && up && (
        <Section label="Bring a colleague in, or send the caller to one">
          <BringColleagueSection callId={call.id} onDone={() => {}} />
        </Section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The column at the right                                                                    */
/* ------------------------------------------------------------------------------------------ */

/** The live transcript in a card of its own — only while there is one to show, as the panel's
 *  own section (`TranscriptPanel`) decides. */
function TranscriptCard() {
  const t = useCallTranscription();
  const visible = t.on || t.pending || t.error !== null || t.finals.length > 0 || t.partials.length > 0;
  if (!visible) return null;
  return (
    <Card className="overflow-hidden">
      <TranscriptPanel />
    </Card>
  );
}

/** The day's own calls, newest first, and the way to the full history. */
function MyCallsTodayCard({ filters, mine }: { filters: CallFilters; mine: readonly string[] }) {
  const list = useCalls(filters, 0, { refetchInterval: 60_000 });
  const total = list.data?.total ?? 0;
  const rows = (list.data?.items ?? []).slice(0, 6);
  return (
    <Card>
      <CardHeader
        title="My calls today"
        description={total === 0 ? undefined : `${total} ${total === 1 ? "call" : "calls"}`}
      />
      <HistoryList
        rows={rows}
        mine={mine}
        pending={list.isPending}
        error={list.error}
        empty="No calls yet today."
        dense
      />
      <div className="border-t border-border px-4 py-2 text-xs">
        <NavLink to="/history" className="inline-flex items-center gap-1.5 font-medium text-accent-11 hover:underline">
          <History className="size-3.5" aria-hidden />
          {total > rows.length ? `${total - rows.length} more in Call history` : "Call history"}
        </NavLink>
      </div>
    </Card>
  );
}

/** Legs of the rep's own history as rows: the other party over what happened, the time at the
 *  right, and room for one control. */
function HistoryList({
  rows, mine, pending, error, empty, dense = false, action,
}: {
  rows: Cdr[];
  mine: readonly string[];
  pending: boolean;
  error: unknown;
  empty: string;
  dense?: boolean;
  action?: (leg: Cdr, party: { number: string | null; name: string | null }) => React.ReactNode;
}) {
  if (pending) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }
  if (error) {
    return (
      <p className="px-4 py-3 text-xs text-text-muted">
        {error instanceof ApiError ? error.message : "Could not load your calls."}
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className={cn("text-xs text-text-muted", dense ? "px-4 py-3" : "py-2")}>{empty}</p>;
  }
  return (
    <ul className={cn("divide-y divide-border", !dense && "rounded-md border border-border")}>
      {rows.map((leg) => {
        const party = counterparty(leg, mine);
        const who = party.name ?? (party.number ? formatPhoneNumber(party.number) : "Unknown");
        return (
          <li key={leg.id} className={cn("flex items-center gap-2.5", dense ? "px-4 py-2" : "px-3 py-2")}>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-text">{who}</span>
              <span className="block truncate text-xs text-text-muted">{legText(leg, mine)}</span>
            </span>
            <span className="shrink-0 text-xs tabular text-text-subtle" title={leg.start_time ?? undefined}>
              {clockTime(leg.start_time)}
            </span>
            {action?.(leg, party)}
          </li>
        );
      })}
    </ul>
  );
}
